// Email Express — inline (in-canvas) hero editing primitives.
//
// Self-contained mirror of the LP Express editor UX (EditableText /
// LpBlockRegenerate / LpImageActions / FloatingPanel) scoped to the email
// editor's hero block. These render editing chrome ONLY when the shared
// renderer (client/src/lib/email-render.tsx) is handed an `editor` object —
// which the headless send / PNG-export paths never pass — so no chrome can ever
// leak into the compiled MJML or the captured email image.
//
// Kept deliberately decoupled: this module imports nothing from email-render, so
// email-render can import these components + the EmailHeroEditor type without a
// cycle.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Images,
  LayoutTemplate,
  Link2,
  Loader2,
  Plus,
  RotateCw,
  Sparkles,
  Star,
  Upload,
  X,
} from "lucide-react";
import type { EmailInstanceCopy, UniversalSocialLink } from "@shared/schema";
import { validateFooterLinkUrl } from "@shared/schema";

// Callbacks the email editor injects to make the preview editable in-canvas. Its
// mere presence on the render context is what switches every block from
// read-only (send / export) to editable (editor) — so it is ALWAYS absent off
// the editor, which is what keeps the editing chrome out of the sent email.
export type EmailEditor = {
  /** Persist a new headline. Wired to the editor's `headline` state setter so
   *  the existing debounced autosave (keyed on `headline`) picks it up. */
  onEditHeadline: (value: string) => void;
  /** Re-run the hero COPY workflow (block="hero") with optional free-text
   *  art-direction feedback, via the existing /regenerate endpoint. */
  onRegenerateHeroCopy: (feedback: string) => void;
  isRegeneratingHeroCopy: boolean;
  /** Re-roll the hero IMAGE with optional art-direction feedback (threaded into
   *  the W13 Hero Image Art Director Sim workflow). */
  onRegenerateHeroImage: (feedback: string) => void;
  /** Upload a custom hero image file (persisted durably, then set as the hero). */
  onUploadHeroImage: (file: File) => void;
  /** Point the hero at an existing product photo URL. */
  onChooseHeroPhoto: (url: string) => void;
  /** Product photos available to "Choose". Empty = the picker is hidden. */
  productPhotos: string[];
  /** True while any hero-IMAGE action (regen / upload / choose) is in flight. */
  heroImageBusy: boolean;
  /** The block INSTANCE id currently selected in the editor (or null). Used to
   *  keep the hero's in-canvas chrome visible while the hero is selected —
   *  discoverable without hover, consistent with the image-block panel picker
   *  (Task #2329). */

  /** Persist the body copy (top-level field). */
  onEditBodyCopy: (value: string) => void;
  /** Persist the CTA button label (top-level field, shared by hero + body). */
  onEditCtaText: (value: string) => void;
  /** Persist a section's punchline (heading), keyed by block INSTANCE id. */
  onEditPunchline: (blockKey: string, value: string) => void;
  /** Merge a patch into a section's per-instance copy, keyed by INSTANCE id. */
  onPatchInstanceCopy: (blockKey: string, patch: Partial<EmailInstanceCopy>) => void;
  /** Re-run the copy workflow for one block (block + sections), with optional
   *  free-text feedback threaded into the block's regen prompt. */
  onRegenerateBlock: (blockKey: string, feedback?: string) => void;
  /** The block INSTANCE id currently regenerating, or null. */
  regeneratingBlockKey: string | null;
  /** Cycle a block instance to its next layout variant (visual only — copy is
   *  unchanged). Live-previews via the per-instance layoutVariant. */
  onChangeLayout: (blockKey: string) => void;
  /** The product's real saved reviews, offered in the inline review picker so a
   *  Reviews block can feature/unfeature them. Empty = the picker stays hidden. */
  availableReviews: { quote: string; author: string; rating: number }[];

  /** Upload a custom image for an image_only / image_discount block instance
   *  (durable R2 upload, then patched onto that instance's copy — a plain
   *  autosaved edit that never triggers polish/regeneration). */
  onUploadBlockImage?: (blockKey: string, file: File) => void;
  /** The block INSTANCE id whose image upload is in flight, or null. */
  blockImageBusyKey?: string | null;

  /** Footer editing callbacks. Each persists a per-email footer override (merged
   *  over the snapshot at render time); present ONLY off the export path so the
   *  editing chrome never reaches the sent email. The FooterBlock builds the new
   *  effective social list itself (it has the merged footer) and hands the full
   *  list here — a present list fully replaces the snapshot's (which is how label
   *  edits, URL edits, and per-item deletion are all represented). */
  footer?: {
    /** Replace the effective social-links list (label/URL edit or deletion). */
    onSetSocialLinks: (links: UniversalSocialLink[]) => void;
    /** Set the disclaimer text override (un-deletes if it was deleted). */
    onSetDisclaimer: (text: string) => void;
    /** Hide the disclaimer in both the preview and the sent email. */
    onDeleteDisclaimer: () => void;
    /** Set the unsubscribe URL override (blank = platform fallback). */
    onSetUnsubscribeUrl: (url: string) => void;
    /** Set the Preferences link label override (un-deletes if it was deleted). */
    onSetPreferencesLabel: (label: string) => void;
    /** Set the Preferences link URL override (blank = platform fallback). */
    onSetPreferencesUrl: (url: string) => void;
    /** Hide the Preferences link on both the preview and the sent email. */
    onDeletePreferences: () => void;
    /** Set the Contact link label override (un-deletes if it was deleted). */
    onSetContactLabel: (label: string) => void;
    /** Set the Contact link URL override (blank = plain text, no anchor). */
    onSetContactUrl: (url: string) => void;
    /** Hide the Contact link on both the preview and the sent email. */
    onDeleteContact: () => void;
  };
};

// Back-compat alias for the original hero-only name.
export type EmailHeroEditor = EmailEditor;

// ── Portaled floating panel ──────────────────────────────────────────────────
// Fixed-position popover portaled to the anchor's own document.body so it is
// never clipped by the hero's `overflow-hidden`, and stays anchored on
// scroll/resize. Mirrors LP Express's FloatingPanel.
function EmailFloatingPanel({
  anchorRef,
  open,
  placement = "bottom",
  align = "right",
  width,
  gap = 6,
  panelRef,
  className,
  onClick,
  children,
  testId,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  placement?: "top" | "bottom";
  align?: "left" | "right";
  width: number;
  gap?: number;
  panelRef?: React.Ref<HTMLDivElement>;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  testId?: string;
}) {
  const [style, setStyle] = useState<React.CSSProperties | null>(null);
  // Measured panel height drives auto-flip + clamping so a panel placed near the
  // viewport bottom is never cut off (the original code clamped only `top`).
  const [panelH, setPanelH] = useState(0);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node;
      if (typeof panelRef === "function") panelRef(node);
      else if (panelRef) (panelRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [panelRef],
  );

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      setPanelH(0);
      return;
    }
    const anchorWin = anchorRef.current?.ownerDocument?.defaultView ?? window;
    const compute = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = anchorWin.innerHeight;
      const maxLeft = anchorWin.innerWidth - width - 8;
      const left =
        align === "right"
          ? Math.max(8, Math.min(r.right - width, maxLeft))
          : Math.max(8, Math.min(r.left, maxLeft));
      const s: React.CSSProperties = { position: "fixed", left, width, zIndex: 9999 };
      // Auto-flip to the side with more room when the requested side can't fit
      // the measured panel; once a side is chosen, clamp so the whole panel stays
      // within [8, vh - 8].
      const spaceBelow = vh - r.bottom - gap;
      const spaceAbove = r.top - gap;
      let placeBottom = placement !== "top";
      if (panelH) {
        if (placeBottom && spaceBelow < panelH && spaceAbove > spaceBelow) placeBottom = false;
        else if (!placeBottom && spaceAbove < panelH && spaceBelow > spaceAbove) placeBottom = true;
      }
      if (placeBottom) {
        let top = r.bottom + gap;
        if (panelH) top = Math.min(top, vh - 8 - panelH);
        s.top = Math.max(8, top);
      } else {
        let bottom = vh - r.top + gap;
        if (panelH) bottom = Math.min(bottom, vh - 8 - panelH);
        s.bottom = Math.max(8, bottom);
      }
      setStyle(s);
    };
    compute();
    anchorWin.addEventListener("scroll", compute, true);
    anchorWin.addEventListener("resize", compute);
    return () => {
      anchorWin.removeEventListener("scroll", compute, true);
      anchorWin.removeEventListener("resize", compute);
    };
  }, [open, placement, align, width, gap, anchorRef, panelH]);

  // After the panel mounts, measure its height so the next compute pass can flip
  // / clamp it. Guarded by a real change to avoid a re-render loop.
  useLayoutEffect(() => {
    if (!open) return;
    const h = innerRef.current?.offsetHeight ?? 0;
    if (h && h !== panelH) setPanelH(h);
  });

  const portalDoc =
    anchorRef.current?.ownerDocument ?? (typeof document !== "undefined" ? document : null);
  if (!open || !style || !portalDoc) return null;
  return createPortal(
    <div ref={setRefs} style={style} className={className} onClick={onClick} data-testid={testId}>
      {children}
    </div>,
    portalDoc.body,
  );
}

// Close a panel on an outside pointerdown / scroll / Escape. The panel is
// portaled out of the anchor's subtree, so clicks inside it count as "inside".
function useDismissOnOutside(
  open: boolean,
  close: () => void,
  refs: Array<React.RefObject<HTMLElement | null>>,
) {
  useEffect(() => {
    if (!open) return;
    const doc = refs[0]?.current?.ownerDocument ?? document;
    const win = doc.defaultView ?? window;
    const onDown = (e: Event) => {
      const t = e.target as Node;
      if (refs.some((r) => r.current?.contains(t))) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    doc.addEventListener("pointerdown", onDown, true);
    win.addEventListener("scroll", close, true);
    doc.addEventListener("keydown", onKey, true);
    return () => {
      doc.removeEventListener("pointerdown", onDown, true);
      win.removeEventListener("scroll", close, true);
      doc.removeEventListener("keydown", onKey, true);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
}

// ── Click-to-edit text ───────────────────────────────────────────────────────
// Uncontrolled contentEditable that commits the trimmed text on blur/Enter and
// never persists an unchanged click (so seeding the placeholder over an empty
// value can't turn the placeholder into real content). Mirrors LP's EditableText
// but trimmed to the email hero's needs (no hide/delete, no formatting toolbar).
export function EmailEditableText({
  as = "span",
  value,
  onCommit,
  className,
  style,
  placeholder,
  multiline = false,
  testId,
}: {
  as?: keyof JSX.IntrinsicElements;
  value: string | null | undefined;
  onCommit: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  multiline?: boolean;
  testId?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const [editing, setEditing] = useState(false);
  const cancelRef = useRef(false);
  const seededRef = useRef("");

  useEffect(() => {
    if (!editing || !ref.current) return;
    const el = ref.current;
    const raw = value ?? "";
    const seed = raw !== "" ? raw : placeholder ?? "";
    seededRef.current = seed;
    const hasElementChildren = Array.from(el.childNodes).some(
      (n) => n.nodeType === Node.ELEMENT_NODE,
    );
    if (!hasElementChildren && el.textContent !== seed) {
      el.textContent = seed;
    }
    el.focus();
    const doc = el.ownerDocument;
    const range = doc.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = doc.defaultView?.getSelection() ?? null;
    sel?.removeAllRanges();
    sel?.addRange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const display = value || placeholder || "";

  const commit = () => {
    const el = ref.current;
    setEditing(false);
    if (!el) return;
    const next = (el.textContent ?? "").trim();
    if (next === seededRef.current.trim()) return;
    if (next !== (value ?? "").trim()) onCommit(next);
  };

  if (editing) {
    return React.createElement(as, {
      key: "email-edit",
      ref,
      className: `${className ?? ""} outline outline-2 outline-dashed outline-primary rounded-sm`,
      style: { ...style, whiteSpace: multiline ? "pre-wrap" : undefined, cursor: "text" },
      contentEditable: true,
      suppressContentEditableWarning: true,
      "data-testid": testId,
      onBlur: () => {
        if (cancelRef.current) {
          cancelRef.current = false;
          setEditing(false);
        } else {
          commit();
        }
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !(multiline && e.shiftKey)) {
          e.preventDefault();
          ref.current?.blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancelRef.current = true;
          ref.current?.blur();
        }
      },
    });
  }

  return React.createElement(
    as,
    {
      key: "email-view",
      ref,
      className: `${className ?? ""} cursor-text rounded-sm hover:outline hover:outline-1 hover:outline-dashed hover:outline-primary/60`,
      style,
      "data-testid": testId,
      title: "Click to edit",
      onClick: () => setEditing(true),
    },
    display,
  );
}

// Small dark pill button used for the hover-revealed hero controls so they read
// against a (usually dark) hero image.
const PILL_CLS =
  "inline-flex items-center gap-1 rounded-md border border-white/30 bg-black/55 px-2 py-1 text-[11px] font-semibold text-white shadow-md backdrop-blur-sm hover:bg-black/70 disabled:opacity-40";

// ── Hero copy regenerate ─────────────────────────────────────────────────────
// A "Regenerate copy" pill that opens a feedback popover and re-runs the hero
// copy workflow (block="hero"). Mirrors LP's LpBlockRegenerate.
export function EmailHeroCopyRegenerate({
  onRegenerate,
  isRegenerating,
}: {
  onRegenerate: (feedback: string) => void;
  isRegenerating: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useDismissOnOutside(open, () => setOpen(false), [rootRef, panelRef]);

  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <span ref={rootRef} contentEditable={false} className="relative inline-flex" data-export-exclude>
      <button
        type="button"
        disabled={isRegenerating}
        className={PILL_CLS}
        onClick={(e) => {
          stop(e);
          setOpen((o) => !o);
        }}
        title="Regenerate hero copy"
        data-testid="button-regenerate-hero-copy"
      >
        {isRegenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
        {isRegenerating ? "Regenerating…" : "Regenerate copy"}
      </button>
      <EmailFloatingPanel
        anchorRef={rootRef as React.RefObject<HTMLElement | null>}
        panelRef={panelRef}
        open={open}
        placement="bottom"
        align="right"
        width={264}
        onClick={stop}
        className="block cursor-default rounded-lg border border-border bg-white p-3 text-left shadow-xl"
        testId="popover-regenerate-hero-copy"
      >
        <div className="mb-1 text-xs font-semibold text-foreground">Regenerate hero copy</div>
        <p className="mb-2 text-[11px] leading-snug text-muted-foreground">
          Rewrites the hero headline. Add an optional note to steer the rewrite.
        </p>
        <textarea
          rows={3}
          value={feedback}
          onClick={stop}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Optional feedback (e.g. punchier, focus on urgency)"
          className="block w-full resize-none rounded border border-border px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary"
          data-testid="input-feedback-hero-copy"
        />
        <button
          type="button"
          disabled={isRegenerating}
          onClick={(e) => {
            stop(e);
            onRegenerate(feedback.trim());
            setOpen(false);
          }}
          className="mt-2 inline-flex h-8 w-full items-center justify-center gap-1 rounded-md bg-primary px-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
          data-testid="button-regenerate-hero-copy-submit"
        >
          {isRegenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
          {isRegenerating ? "Regenerating…" : "Regenerate"}
        </button>
      </EmailFloatingPanel>
    </span>
  );
}

// Light pill for hover-revealed controls over light section backgrounds.
const LIGHT_PILL_CLS =
  "inline-flex items-center gap-1 rounded-md border border-black/10 bg-white/90 px-2 py-1 text-[11px] font-semibold text-neutral-700 shadow-md backdrop-blur-sm hover:bg-white disabled:opacity-40";

// ── Generic block regenerate ─────────────────────────────────────────────────
// A "Regenerate" pill for non-hero regenerable blocks (body + sections) that
// opens the same optional-feedback popover as the hero: the note is threaded
// into the block's regen prompt server-side (blank = same behavior as before).
export function EmailBlockRegenerate({
  onRegenerate,
  isRegenerating,
  tone = "light",
}: {
  onRegenerate: (feedback: string) => void;
  isRegenerating: boolean;
  tone?: "light" | "dark";
}) {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useDismissOnOutside(open, () => setOpen(false), [rootRef, panelRef]);

  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <span ref={rootRef} contentEditable={false} className="relative inline-flex" data-export-exclude>
      <button
        type="button"
        disabled={isRegenerating}
        className={tone === "dark" ? PILL_CLS : LIGHT_PILL_CLS}
        onClick={(e) => {
          stop(e);
          setOpen((o) => !o);
        }}
        title="Regenerate this block"
        data-testid="button-regenerate-block-inline"
      >
        {isRegenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
        {isRegenerating ? "Regenerating…" : "Regenerate"}
      </button>
      <EmailFloatingPanel
        anchorRef={rootRef as React.RefObject<HTMLElement | null>}
        panelRef={panelRef}
        open={open}
        placement="bottom"
        align="right"
        width={264}
        onClick={stop}
        className="block cursor-default rounded-lg border border-border bg-white p-3 text-left shadow-xl"
        testId="popover-regenerate-block-inline"
      >
        <div className="mb-1 text-xs font-semibold text-foreground">Regenerate section</div>
        <p className="mb-2 text-[11px] leading-snug text-muted-foreground">
          Rewrites this section&apos;s copy. Add an optional note to steer the rewrite.
        </p>
        <textarea
          rows={3}
          value={feedback}
          onClick={stop}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Optional feedback (e.g. shorter, focus on benefits)"
          className="block w-full resize-none rounded border border-border px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary"
          data-testid="input-feedback-block-inline"
        />
        <button
          type="button"
          disabled={isRegenerating}
          onClick={(e) => {
            stop(e);
            onRegenerate(feedback.trim());
            setOpen(false);
          }}
          className="mt-2 inline-flex h-8 w-full items-center justify-center gap-1 rounded-md bg-primary px-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
          data-testid="button-regenerate-block-inline-submit"
        >
          {isRegenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
          {isRegenerating ? "Regenerating…" : "Regenerate"}
        </button>
      </EmailFloatingPanel>
    </span>
  );
}

// One-click "Change layout" pill: cycles a block to its next layout variant
// (visual expression only — the copy is untouched). Lives in the per-block edit
// chrome next to Regenerate and, like it, is marked data-export-exclude so it
// can never reach the captured email image.
export function EmailLayoutCycle({
  onChange,
  tone = "light",
}: {
  onChange: () => void;
  tone?: "light" | "dark";
}) {
  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <span contentEditable={false} className="relative inline-flex" data-export-exclude>
      <button
        type="button"
        className={tone === "dark" ? PILL_CLS : LIGHT_PILL_CLS}
        onClick={(e) => {
          stop(e);
          onChange();
        }}
        title="Change layout"
        data-testid="button-change-layout-inline"
      >
        <LayoutTemplate className="h-3 w-3" />
        Change layout
      </button>
    </span>
  );
}

// ── Hero image actions ───────────────────────────────────────────────────────
// Overlay with three actions: AI Regenerate (with art-direction feedback),
// Upload a custom image, and Choose an existing product photo. Mirrors LP's
// LpImageActions. The hidden file <input> is mounted OUTSIDE every popover so
// reopening a popover never remounts/clears it.
export function EmailHeroImageActions({
  onRegenerate,
  onUpload,
  onChoose,
  photos,
  busy,
  className,
}: {
  onRegenerate: (feedback: string) => void;
  onUpload: (file: File) => void;
  onChoose: (url: string) => void;
  photos: string[];
  busy: boolean;
  className?: string;
}) {
  const [regenOpen, setRegenOpen] = useState(false);
  const [chooseOpen, setChooseOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const regenRef = useRef<HTMLDivElement>(null);
  const regenPanelRef = useRef<HTMLDivElement>(null);
  const chooseRef = useRef<HTMLDivElement>(null);
  const choosePanelRef = useRef<HTMLDivElement>(null);
  useDismissOnOutside(regenOpen, () => setRegenOpen(false), [regenRef, regenPanelRef]);
  useDismissOnOutside(chooseOpen, () => setChooseOpen(false), [chooseRef, choosePanelRef]);

  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      className={`flex items-center gap-1.5 ${className ?? ""}`}
      data-export-exclude
      onClick={stop}
      data-testid="hero-image-actions"
    >
      {/* Hidden uploader — kept outside the popovers so it never unmounts. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        data-testid="input-upload-hero-image"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onUpload(file);
        }}
      />

      {/* AI regenerate with feedback */}
      <div ref={regenRef} className="relative">
        <button
          type="button"
          disabled={busy}
          className={PILL_CLS}
          onClick={(e) => {
            stop(e);
            setRegenOpen((o) => !o);
            setChooseOpen(false);
          }}
          title="Regenerate hero image"
          data-testid="button-regenerate-hero-image"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
          Regenerate
        </button>
        <EmailFloatingPanel
          anchorRef={regenRef as React.RefObject<HTMLElement | null>}
          panelRef={regenPanelRef}
          open={regenOpen}
          placement="bottom"
          align="right"
          width={264}
          onClick={stop}
          className="block cursor-default rounded-lg border border-border bg-white p-3 text-left shadow-xl"
          testId="popover-regenerate-hero-image"
        >
          <div className="mb-1 text-xs font-semibold text-foreground">Regenerate hero image</div>
          <p className="mb-2 text-[11px] leading-snug text-muted-foreground">
            Rolls a fresh AI hero photo. Add an optional art-direction note.
          </p>
          <textarea
            rows={3}
            value={feedback}
            onClick={stop}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="e.g. warmer light, product held in hands, outdoor setting"
            className="block w-full resize-none rounded border border-border px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary"
            data-testid="input-feedback-hero-image"
          />
          <button
            type="button"
            disabled={busy}
            onClick={(e) => {
              stop(e);
              onRegenerate(feedback.trim());
              setRegenOpen(false);
            }}
            className="mt-2 inline-flex h-8 w-full items-center justify-center gap-1 rounded-md bg-primary px-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
            data-testid="button-regenerate-hero-image-submit"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
            Regenerate
          </button>
        </EmailFloatingPanel>
      </div>

      {/* Upload custom image */}
      <button
        type="button"
        disabled={busy}
        className={PILL_CLS}
        onClick={(e) => {
          stop(e);
          fileRef.current?.click();
        }}
        title="Upload a custom hero image"
        data-testid="button-upload-hero-image"
      >
        <Upload className="h-3.5 w-3.5" />
        Upload
      </button>

      {/* Choose an existing product photo */}
      {photos.length > 0 && (
        <div ref={chooseRef} className="relative">
          <button
            type="button"
            disabled={busy}
            className={PILL_CLS}
            onClick={(e) => {
              stop(e);
              setChooseOpen((o) => !o);
              setRegenOpen(false);
            }}
            title="Choose a product photo"
            data-testid="button-choose-hero-photo"
          >
            <Images className="h-3.5 w-3.5" />
            Choose
          </button>
          <EmailFloatingPanel
            anchorRef={chooseRef as React.RefObject<HTMLElement | null>}
            panelRef={choosePanelRef}
            open={chooseOpen}
            placement="bottom"
            align="right"
            width={256}
            onClick={stop}
            className="rounded-lg border border-border bg-white p-2 shadow-xl"
            testId="popover-choose-hero-photo"
          >
            <p className="mb-1.5 px-0.5 text-[11px] font-semibold text-foreground/80">Product photos</p>
            <div className="grid max-h-60 grid-cols-3 gap-1.5 overflow-y-auto">
              {photos.map((url, i) => (
                <button
                  key={`${i}-${url}`}
                  type="button"
                  disabled={busy}
                  onClick={(e) => {
                    stop(e);
                    onChoose(url);
                    setChooseOpen(false);
                  }}
                  title="Use this product photo"
                  data-testid={`button-choose-hero-photo-option-${i}`}
                  className="relative aspect-square overflow-hidden rounded-md border border-border bg-muted hover:border-primary disabled:opacity-40"
                >
                  <img src={url} alt={`Product photo ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </EmailFloatingPanel>
        </div>
      )}
    </div>
  );
}

// ── Block image actions (image_only / image_discount) ───────────────────────
// Overlay with Upload / Choose / Reset actions for the per-instance image
// selection on image blocks (Task #2286). Mirrors EmailHeroImageActions minus
// the AI regenerate — these blocks show REAL photos only. The hidden file
// <input> is mounted outside the popover so reopening never remounts/clears it.
export function EmailBlockImageActions({
  onUpload,
  onChoose,
  onReset,
  hasSelection,
  photos,
  busy,
}: {
  onUpload: (file: File) => void;
  onChoose: (url: string) => void;
  /** Clear the selection back to the default first product photo. */
  onReset: () => void;
  /** True when the instance carries an explicit selection (shows Reset). */
  hasSelection: boolean;
  photos: string[];
  busy: boolean;
}) {
  const [chooseOpen, setChooseOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const chooseRef = useRef<HTMLDivElement>(null);
  const choosePanelRef = useRef<HTMLDivElement>(null);
  useDismissOnOutside(chooseOpen, () => setChooseOpen(false), [chooseRef, choosePanelRef]);

  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      className="flex items-center gap-1.5"
      data-export-exclude
      onClick={stop}
      data-testid="block-image-actions"
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        data-testid="input-upload-block-image"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onUpload(file);
        }}
      />
      <button
        type="button"
        disabled={busy}
        className={PILL_CLS}
        onClick={(e) => {
          stop(e);
          fileRef.current?.click();
        }}
        title="Upload a custom image"
        data-testid="button-upload-block-image"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        Upload
      </button>
      {photos.length > 0 && (
        <div ref={chooseRef} className="relative">
          <button
            type="button"
            disabled={busy}
            className={PILL_CLS}
            onClick={(e) => {
              stop(e);
              setChooseOpen((o) => !o);
            }}
            title="Choose a product photo"
            data-testid="button-choose-block-image"
          >
            <Images className="h-3.5 w-3.5" />
            Choose
          </button>
          <EmailFloatingPanel
            anchorRef={chooseRef as React.RefObject<HTMLElement | null>}
            panelRef={choosePanelRef}
            open={chooseOpen}
            placement="bottom"
            align="right"
            width={256}
            onClick={stop}
            className="rounded-lg border border-border bg-white p-2 shadow-xl"
            testId="popover-choose-block-image"
          >
            <p className="mb-1.5 px-0.5 text-[11px] font-semibold text-foreground/80">Product photos</p>
            <div className="grid max-h-60 grid-cols-3 gap-1.5 overflow-y-auto">
              {photos.map((url, i) => (
                <button
                  key={`${i}-${url}`}
                  type="button"
                  disabled={busy}
                  onClick={(e) => {
                    stop(e);
                    onChoose(url);
                    setChooseOpen(false);
                  }}
                  title="Use this product photo"
                  data-testid={`button-choose-block-image-option-${i}`}
                  className="relative aspect-square overflow-hidden rounded-md border border-border bg-muted hover:border-primary disabled:opacity-40"
                >
                  <img src={url} alt={`Product photo ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </EmailFloatingPanel>
        </div>
      )}
      {hasSelection && (
        <button
          type="button"
          disabled={busy}
          className={PILL_CLS}
          onClick={(e) => {
            stop(e);
            onReset();
          }}
          title="Reset to the default product photo"
          data-testid="button-reset-block-image"
        >
          <RotateCw className="h-3.5 w-3.5" />
          Reset
        </button>
      )}
    </div>
  );
}

// ── Subject lines + inbox preview text editor ────────────────────────────────
// Editor-only modal body (no trigger/centering of its own) mounted inside a
// centered top-toolbar Dialog, so subject lines + the inbox preview snippet are
// edited away from the rendered email instead of pinned above it. The active
// subject is subjectLines[0]; the A/B/C… letters / "Use" buttons switch which
// variant is primary; the Regenerate box rebuilds the set (count + optional
// steering feedback). None of this is ever part of the captured/sent email.
export function EmailSubjectPreviewPanel({
  subjectLines,
  onChangeSubjectLines,
  previewText,
  onChangePreviewText,
  regenCount,
  onChangeRegenCount,
  feedback,
  onChangeFeedback,
  onRegenerate,
  regenerating,
  senderName,
  onDone,
}: {
  subjectLines: string[];
  onChangeSubjectLines: (lines: string[]) => void;
  previewText: string;
  onChangePreviewText: (value: string) => void;
  regenCount: string;
  onChangeRegenCount: (value: string) => void;
  feedback: string;
  onChangeFeedback: (value: string) => void;
  onRegenerate: () => void;
  regenerating: boolean;
  senderName: string;
  onDone: () => void;
}) {
  const pick = (i: number) =>
    onChangeSubjectLines([subjectLines[i], ...subjectLines.filter((_, idx) => idx !== i)]);
  const editVariant = (i: number, value: string) =>
    onChangeSubjectLines(subjectLines.map((v, idx) => (idx === i ? value : v)));
  const removeVariant = (i: number) =>
    onChangeSubjectLines(subjectLines.filter((_, idx) => idx !== i));
  const addVariant = () => onChangeSubjectLines([...subjectLines, ""]);
  const activeSubject = subjectLines[0] ?? "";
  const avatarLetter = (senderName?.trim()?.charAt(0) || "P").toUpperCase();
  const trunc = (s: string, n: number) =>
    s.length > n ? `${s.slice(0, Math.max(0, n - 1)).trimEnd()}…` : s;
  return (
    <div className="text-left" data-testid="panel-subject-preview" data-export-exclude>
      {/* Subject line variants */}
      <section>
        <h3 className="text-sm font-semibold text-foreground">Subject line variants</h3>
        <div className="mt-2 space-y-1.5">
          {subjectLines.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No subject lines yet — regenerate below.
            </p>
          )}
          {subjectLines.map((line, i) => {
            const active = i === 0;
            return (
              <div
                key={i}
                className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${
                  active ? "border-primary/50 bg-primary/5" : "border-border"
                }`}
                data-testid={`manage-subject-row-${i}`}
              >
                <button
                  type="button"
                  onClick={() => pick(i)}
                  title={active ? "Active subject line" : "Use this subject line"}
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                    active
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                  data-testid={`button-manage-pick-${i}`}
                >
                  {String.fromCharCode(65 + i)}
                </button>
                <input
                  value={line}
                  onChange={(e) => editVariant(i, e.target.value)}
                  placeholder={`Subject line ${i + 1}`}
                  className="h-7 flex-1 rounded border border-transparent bg-transparent px-1.5 text-xs outline-none hover:border-border focus:border-primary"
                  data-testid={`input-manage-subject-${i}`}
                />
                {active ? (
                  <span
                    className="flex shrink-0 items-center gap-1 px-1 text-[11px] font-semibold text-primary"
                    data-testid={`text-active-${i}`}
                  >
                    <Check className="h-3.5 w-3.5" /> Active
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => pick(i)}
                    className="shrink-0 rounded-md border border-primary/40 px-2 py-0.5 text-[11px] font-semibold text-primary hover:bg-primary/10"
                    data-testid={`button-use-${i}`}
                  >
                    Use
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeVariant(i)}
                  className="shrink-0 text-muted-foreground/60 hover:text-destructive"
                  title="Remove variant"
                  data-testid={`button-manage-remove-${i}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={addVariant}
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            data-testid="button-add-subject-variant"
          >
            <Plus className="h-3.5 w-3.5" /> Add variant
          </button>
          <div className="flex items-center gap-2">
            <div
              className="flex items-center gap-1"
              title="How many subject lines to generate"
            >
              {["2", "4", "6"].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => onChangeRegenCount(n)}
                  className={`h-7 w-7 rounded-md text-xs font-semibold ${
                    regenCount === n
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                  data-testid={`button-variant-count-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={regenerating}
              onClick={onRegenerate}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-40"
              data-testid="button-regenerate-variants"
            >
              {regenerating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              Regenerate subject lines
            </button>
          </div>
        </div>
        <input
          value={feedback}
          onChange={(e) => onChangeFeedback(e.target.value)}
          placeholder="Optional: steer the tone or angle of regenerated lines"
          className="mt-2 h-8 w-full rounded border border-border px-2 text-xs outline-none focus:border-primary"
          data-testid="input-variant-feedback"
        />
      </section>

      {/* Preview text */}
      <section className="mt-4">
        <label
          htmlFor="email-preview-text"
          className="text-sm font-semibold text-foreground"
        >
          Preview text
        </label>
        <textarea
          id="email-preview-text"
          rows={3}
          value={previewText}
          onChange={(e) => onChangePreviewText(e.target.value)}
          placeholder="Add inbox preview text…"
          className="mt-1.5 w-full resize-none rounded-md border border-border px-2.5 py-2 text-xs leading-snug outline-none focus:border-primary"
          data-testid="input-preview-text"
        />
        <div
          className="mt-1 text-right text-[11px] text-muted-foreground"
          data-testid="text-preview-count"
        >
          {previewText.length} / 140
        </div>
      </section>

      {/* Inbox preview */}
      <section className="mt-3">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Inbox preview
        </div>
        <div
          className="mt-1.5 flex items-center gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2.5"
          data-testid="inbox-preview"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
            {avatarLetter}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span
                className="truncate text-xs font-semibold text-foreground"
                data-testid="text-inbox-sender"
              >
                {senderName || "Sender"}
              </span>
              <span className="shrink-0 text-[11px] text-muted-foreground">9:41 AM</span>
            </div>
            <div
              className="truncate text-xs font-medium text-foreground"
              data-testid="text-inbox-subject"
            >
              {trunc(activeSubject, 52) || "Your subject line"}
            </div>
            <div
              className="truncate text-[11px] text-muted-foreground"
              data-testid="text-inbox-preview"
            >
              {trunc(previewText, 60) || "Your preview text appears here"}
            </div>
          </div>
        </div>
      </section>

      <div className="mt-5 flex items-center justify-end border-t border-border pt-3">
        <button
          type="button"
          onClick={onDone}
          className="inline-flex h-9 items-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          data-testid="button-done"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ── Inline list controls ─────────────────────────────────────────────────────
// Small editor-only "+ Add" button used at the foot of a list/section block.
export function EmailInlineAddButton({
  label,
  onClick,
  testId,
}: {
  label: string;
  onClick: () => void;
  testId?: string;
}) {
  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <button
      type="button"
      contentEditable={false}
      data-export-exclude
      onClick={(e) => {
        stop(e);
        onClick();
      }}
      className="inline-flex items-center gap-1 rounded-md border border-dashed border-black/20 bg-white/80 px-3 py-1.5 text-[11px] font-semibold text-neutral-600 hover:bg-white"
      data-testid={testId}
    >
      <Plus className="h-3 w-3" /> {label}
    </button>
  );
}

// Hover-revealed reorder + remove cluster for a list item. The containing item
// must be `group/item` for the hover reveal to work.
export function EmailListItemControls({
  keyId,
  onMoveUp,
  onMoveDown,
  onRemove,
  canMoveUp,
  canMoveDown,
}: {
  keyId: string;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onRemove: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}) {
  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const btn =
    "flex h-5 w-5 items-center justify-center rounded text-neutral-500 hover:bg-black/5 hover:text-neutral-800 disabled:opacity-30 disabled:hover:bg-transparent";
  return (
    <span
      contentEditable={false}
      data-export-exclude
      className="ml-auto flex shrink-0 items-center gap-0.5 self-center opacity-0 transition-opacity group-hover/item:opacity-100"
    >
      {onMoveUp && (
        <button
          type="button"
          disabled={!canMoveUp}
          onClick={(e) => {
            stop(e);
            onMoveUp();
          }}
          className={btn}
          title="Move up"
          data-testid={`button-item-up-${keyId}`}
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
      )}
      {onMoveDown && (
        <button
          type="button"
          disabled={!canMoveDown}
          onClick={(e) => {
            stop(e);
            onMoveDown();
          }}
          className={btn}
          title="Move down"
          data-testid={`button-item-down-${keyId}`}
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        type="button"
        onClick={(e) => {
          stop(e);
          onRemove();
        }}
        className={`${btn} hover:text-destructive`}
        title="Remove"
        data-testid={`button-item-remove-${keyId}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

// Hover-revealed remove button pinned to a cell/card corner. The containing
// element must be `group/item relative`.
export function EmailCornerRemove({
  keyId,
  onRemove,
  className,
}: {
  keyId: string;
  onRemove: () => void;
  className?: string;
}) {
  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <button
      type="button"
      contentEditable={false}
      data-export-exclude
      onClick={(e) => {
        stop(e);
        onRemove();
      }}
      title="Remove"
      data-testid={`button-remove-${keyId}`}
      className={`absolute right-1.5 top-1.5 z-20 flex h-6 w-6 items-center justify-center rounded-full border border-black/10 bg-white/90 text-neutral-500 opacity-0 shadow-sm transition-opacity hover:text-destructive group-hover/item:opacity-100 ${className ?? ""}`}
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );
}

// Inline URL editor for an editable footer link (a social link, or the
// unsubscribe link). A small "link" button — rendered as a SIBLING of the anchor
// it edits (never nested, so it can't end up inside the sent <a>) — opens a
// popover with a single URL field. Empty is allowed and means "no/inert link"
// for social rows; for unsubscribe an empty value is the platform-fallback
// sentinel (the caller decides what blank means). Validates with the http(s)-only
// checkout validator so anchors/garbage can't be saved.
export function EmailLinkUrlPopover({
  keyId,
  url,
  onSave,
  label,
}: {
  keyId: string;
  url: string;
  onSave: (url: string) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(url);
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useDismissOnOutside(open, () => setOpen(false), [rootRef, panelRef]);

  useEffect(() => {
    if (open) setDraft(url);
  }, [open, url]);

  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const error = validateFooterLinkUrl(draft);
  const save = () => {
    if (error) return;
    onSave(draft.trim());
    setOpen(false);
  };

  return (
    <span ref={rootRef} contentEditable={false} data-export-exclude className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => {
          stop(e);
          setOpen((o) => !o);
        }}
        title={`Edit ${label} URL`}
        data-testid={`button-edit-link-${keyId}`}
        className="ml-1 flex h-5 w-5 items-center justify-center rounded text-neutral-400 opacity-0 transition-opacity hover:bg-black/5 hover:text-neutral-700 group-hover/item:opacity-100"
      >
        <Link2 className="h-3.5 w-3.5" />
      </button>
      <EmailFloatingPanel
        anchorRef={rootRef as React.RefObject<HTMLElement | null>}
        panelRef={panelRef}
        open={open}
        placement="bottom"
        align="left"
        width={280}
        onClick={stop}
        className="block cursor-default rounded-lg border border-border bg-white p-3 text-left shadow-xl"
        testId={`popover-edit-link-${keyId}`}
      >
        <div className="mb-1 text-xs font-semibold text-foreground">{label} URL</div>
        <input
          type="text"
          value={draft}
          onClick={stop}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save();
            }
          }}
          placeholder="https://…"
          className="block w-full rounded border border-border px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary"
          data-testid={`input-edit-link-${keyId}`}
        />
        {draft.trim() && error ? (
          <p className="mt-1 text-[11px] leading-snug text-destructive" data-testid={`error-edit-link-${keyId}`}>
            {error}
          </p>
        ) : null}
        <div className="mt-2 flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={(e) => {
              stop(e);
              setOpen(false);
            }}
            className="inline-flex h-7 items-center rounded-md px-2 text-xs font-medium text-muted-foreground hover:bg-black/5"
            data-testid={`button-edit-link-cancel-${keyId}`}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!!(draft.trim() && error)}
            onClick={(e) => {
              stop(e);
              save();
            }}
            className="inline-flex h-7 items-center gap-1 rounded-md bg-primary px-2.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
            data-testid={`button-edit-link-save-${keyId}`}
          >
            <Check className="h-3.5 w-3.5" />
            Save
          </button>
        </div>
      </EmailFloatingPanel>
    </span>
  );
}

// Inline "Feature reviews" picker: a popover listing the product's real saved
// reviews; toggling one features/unfeatures it in the Reviews block. Hidden
// when there are no saved reviews to choose from.
export function EmailReviewPicker({
  available,
  items,
  onChange,
}: {
  available: { quote: string; author: string; rating: number }[];
  items: { quote: string; author: string }[];
  onChange: (items: { quote: string; author: string }[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useDismissOnOutside(open, () => setOpen(false), [rootRef, panelRef]);
  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  if (!available || !available.length) return null;
  const norm = (q: string) => q.trim().toLowerCase();
  const selected = new Set(items.map((it) => norm(it.quote)));
  const toggle = (r: { quote: string; author: string }) => {
    const k = norm(r.quote);
    if (selected.has(k)) onChange(items.filter((it) => norm(it.quote) !== k));
    else onChange([...items, { quote: r.quote, author: r.author }]);
  };
  return (
    <span ref={rootRef} contentEditable={false} data-export-exclude className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => {
          stop(e);
          setOpen((o) => !o);
        }}
        className={LIGHT_PILL_CLS}
        data-testid="button-feature-reviews"
      >
        <Star className="h-3 w-3" /> Feature reviews
      </button>
      <EmailFloatingPanel
        anchorRef={rootRef as React.RefObject<HTMLElement | null>}
        panelRef={panelRef}
        open={open}
        placement="bottom"
        align="left"
        width={300}
        onClick={stop}
        className="rounded-lg border border-border bg-white p-2 shadow-xl"
        testId="popover-feature-reviews"
      >
        <p className="mb-1.5 px-0.5 text-[11px] font-semibold text-foreground/80">Feature real reviews</p>
        <div className="max-h-64 space-y-1.5 overflow-y-auto">
          {available.map((r, i) => {
            const isSel = selected.has(norm(r.quote));
            return (
              <button
                key={i}
                type="button"
                onClick={(e) => {
                  stop(e);
                  toggle(r);
                }}
                className={`flex w-full flex-col gap-0.5 rounded-md border p-2 text-left ${
                  isSel ? "border-primary ring-1 ring-primary" : "border-border/60 hover:bg-muted/40"
                }`}
                data-testid={`button-feature-review-${i}`}
              >
                <span className="flex items-center gap-1">
                  <span className="inline-flex text-amber-500">
                    {Array.from({ length: r.rating }).map((_, s) => (
                      <Star key={s} className="h-2.5 w-2.5 fill-amber-500" />
                    ))}
                  </span>
                  {r.author && (
                    <span className="truncate text-[10px] font-semibold text-muted-foreground">
                      {r.author}
                    </span>
                  )}
                  {isSel && <Check className="ml-auto h-3 w-3 text-primary" />}
                </span>
                <span className="line-clamp-2 text-[11px] leading-snug text-foreground/80">{r.quote}</span>
              </button>
            );
          })}
        </div>
      </EmailFloatingPanel>
    </span>
  );
}
